import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import Card from '@wtag/rcl-card';
import Button from '@wtag/rcl-button';
import Tooltip from '@wtag/rcl-tooltip';
import Icon from '@wtag/rcl-icon';
import { I18nText } from '@wtag/react-comp-lib';
import AirportShape from 'sharedWebpack/IBE/Search/shapes/AirportShape';
import Alert from 'sharedWebpack/Alert';
import feeList from '../lib/feeList';
import CartItemWrapper from './Items/CartItemWrapper';
import Summary from './Summary';
import CartShare from './CartShare';
import CartDeal from './CartDeal';
import AddButtons from './Items/AddButtons';
import PaymentError from './PaymentMessage/PaymentError';
import EmptyCart from './Items/EmptyCart';
import staticProductShape from '../Search/static/shapes/staticProduct';
import CheckoutProgress from '../components/CheckoutProgress';
import { stringifyQueryString, parseQueryString } from '../helpers/qsMethods';
import history from '../lib/history';
import {
  CART_STATUS_BOOKED,
  CART_STATUS_PAID,
  CART_STATUS_OPEN,
  CART_STATUS_LOCKED,
} from '../actions/common';
import ExchangeHeader from '../Search/flight/Exchange/ExchangeHeader';
import ExchangeProgress from '../Search/flight/Exchange/ExchangeProgress';
import { initialSearchParamsShape } from '../config/shapes';
import searchURL from '../lib/helpers/searchURL';
import { CART_STEP_INDEX } from '../helpers/constants';
import ExchangeCartSummary from './ExchangeCartSummary';
import getUnchangedTripIds from '../SearchDetails/flight/helpers/getUnchangedTripIds';
import './styles.scss';

class Cart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldLockScroll: false,
    };
    this.locationQuery = parseQueryString(props.location.search);
    this.orderSummaryRef = createRef();
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    if (!this.props.fees.length) {
      this.props.fetchFees(this.context.callbackParams);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.search !== prevProps.location.search) {
      this.locationQuery = parseQueryString(this.props.location.search);
    }

    if (
      this.props.exchange.canExchange &&
      !this.props.exchangeQuoteData &&
      !this.props.exchangeQuoteStatus &&
      this.props.pastJourneyElement.trips &&
      this.props.items.length > 0 &&
      this.props.journeyElements.flight[this.props.items[0].journeyElementId]
    ) {
      const unchangedTripIds = getUnchangedTripIds(
        this.props.exchange.canExchange,
        this.props.journeyElements.flight[this.props.items[0].journeyElementId].trips,
        this.props.pastJourneyElement.trips,
      );

      this.props.fetchExchangeQuote(
        this.props.exchange.reservationItemIdentifier,
        this.props.items[0]?.bookingAttributes?.resultId,
        unchangedTripIds,
      );
    }
  }

  setFirstConfirmableItem = node => {
    if (node) {
      this.firstConfirmableItem = node;
    }
  };

  cartSharingPossible = () => {
    if (this.context.iframeMode) {
      return false;
    }

    if (this.props.expiredAt) {
      return false;
    }

    return true;
  };

  conditionToDisableNextButton = () =>
    (this.props.exchange.canExchange
      ? !this.props.exchangeQuoteData?.availability
      : !this.props.allItemsAvailable) ||
    this.props.items.length <= 0 ||
    this.props.cartState !== CART_STATUS_OPEN ||
    this.props.cartState === CART_STATUS_LOCKED ||
    (!this.props.continuePossible && this.props.canBookQuoteItems) ||
    this.props.hasItemsWithPendingConfirmation ||
    !!this.props.expiredAt;

  checkoutProgressAttributes = () => {
    const attributes = {
      cartId: this.props.match.params.id,
      currentStep: 'cart',
      cartError: this.props.flightServicesFetched && !this.props.allItemsAvailable,
      nextButtonDisabled: this.conditionToDisableNextButton(),
      totalCartItems: this.props.totalCartItems,
      orderSummaryRef: this.orderSummaryRef,
    };

    if (this.props.forceDeal) {
      attributes.nextButton = (
        <Button
          version="v2"
          size="small"
          label={I18n.t('components.ibe.create_deal')}
          disabled={true}
        />
      );
    }

    if (this.context.offerOnly) {
      attributes.nextButton = (
        <CartDeal
          dealName={this.props.dealName}
          dealTeaser={this.props.dealTeaser}
          setDealName={this.props.setDealName}
          setDealTeaser={this.props.setDealTeaser}
          dealSaveAvailable={this.props.dealSaveAvailable}
          saveDeal={this.props.saveDeal}
          forceDeal={this.props.forceDeal}
        />
      );
    }
    return attributes;
  };

  usedCart = () => (
    <div className="cart-used">
      <Card version="v2">{I18n.t('components.ibe.cart_used')}</Card>
    </div>
  );

  duplicateItemOfSameSearchExist = () => {
    if (this.props.items.length !== 0) {
      const searchIds = this.props.items.map(item => item.search.id);
      return new Set(searchIds).size !== searchIds.length;
    }
    return false;
  };

  viewAlertTextForSharedCart = sharedByCurrentPerson => {
    if (sharedByCurrentPerson) {
      return I18n.t('cart.cart_share.layman_mode.cart_shared_alert.owner');
    }

    return I18n.t('cart.cart_share.layman_mode.cart_shared_alert.non_owner');
  };

  goToSearchResultPage = () => {
    history.push(searchURL('flight', this.props.items[0].search.identifier));
  };

  goToPaymentPage = () => {
    history.push(`/orders/${this.props.cartId}/payment`);
  };

  exchangeView = () =>
    this.props.initialSearchParams.flights && (
      <div className="d-flex-column row-gap-20">
        <ExchangeHeader
          airports={this.props.airports}
          travelers={this.props.initialSearchParams.flights.travelers}
          trips={this.props.initialSearchParams.flights.trips}
          exchange={this.props.exchange}
        />
        <ExchangeProgress
          activeStepIndex={CART_STEP_INDEX}
          disableProceedToNextStep={this.conditionToDisableNextButton()}
          onNextStep={this.goToPaymentPage}
          onPrevStep={this.goToSearchResultPage}
          hasCartError={
            this.props.flightServicesFetched && !this.props.exchangeQuoteData?.availability
          }
        />
      </div>
    );

  render() {
    const { canExchange } = this.props.exchange;
    const fromSharedCarts = this.locationQuery.fromSharedCarts;
    const isCartBooked =
      this.props.cartState === CART_STATUS_BOOKED || this.props.cartState === CART_STATUS_PAID;
    const isCartLocked = this.props.cartState === CART_STATUS_LOCKED;
    const isPassengerCodeAvailable = this.props.items.some(item => item.passengerCode);

    if (isCartBooked && !!fromSharedCarts) {
      history.push({
        pathname: `/orders/${this.props.cartId}/booking`,
        search: stringifyQueryString({ fromSharedCarts }),
      });
    }

    if (this.props.cartState !== CART_STATUS_OPEN && this.props.cartState !== CART_STATUS_LOCKED)
      return this.usedCart();

    const cartShareComponent = (
      <CartShare
        items={this.props.items}
        journeyElements={this.props.journeyElements}
        flightServicesByResultId={this.props.flightServicesByResultId}
        flightResultDetailsById={this.props.flightResultDetailsById}
        travelers={this.props.travelers}
        cartId={this.props.cartId}
        cartUrl={this.props.cartUrl}
        title={this.props.title}
        sharedTitle={this.props.sharedTitle}
        callbackParams={this.context.callbackParams}
        travelArranger={this.context.travelArranger}
        travelerLoggedIn={this.context.travelerLoggedIn}
        laymanMode={this.context.laymanMode}
      />
    );

    const toolTipContent = (
      <div className="cart-share__modal-content">
        <Icon name="info" />
        {I18n.t('cart.cart_share.message_for_free_users')}
      </div>
    );

    return (
      <div>
        {canExchange ? (
          this.exchangeView()
        ) : (
          <CheckoutProgress {...this.checkoutProgressAttributes()} />
        )}
        <div
          className={classNames('container container--sm-full-width cart', {
            'cart__exchange-view': canExchange,
          })}
        >
          <Alert
            type="warning-light"
            isIconVisible={false}
            hideClose={true}
            className="cart-items__card-container-alert"
            isVisible={isCartLocked && this.props.isShared && !this.props.expiredAt}
          >
            {I18n.t('components.ibe.cart_locked')}
          </Alert>
          <Alert
            type="warning-light"
            isIconVisible={false}
            hideClose={true}
            className="cart-items__card-container-alert"
            isVisible={
              this.context.laymanMode &&
              this.props.cartState !== CART_STATUS_LOCKED &&
              this.props.isShared &&
              !this.props.expiredAt &&
              !!fromSharedCarts
            }
          >
            {this.viewAlertTextForSharedCart(this.props.sharedByCurrentPerson)}
          </Alert>
          <Alert
            type="danger-light"
            isIconVisible={false}
            hideClose={true}
            className="cart-items__card-container-alert"
            isVisible={!!this.props.expiredAt}
          >
            {I18n.t('cart.cart_share.layman_mode.cart_expired_alert', {
              date: I18n.l('date.formats.date_month_year_short', this.props.expiredAt),
            })}
          </Alert>
          {this.locationQuery.paymentError && (
            <PaymentError messageKey={this.locationQuery.paymentError} />
          )}
          <div className="grid grid-gap-20 cart-items__card-container">
            <div className="col-lg-8 col-grid cart-items__card-container">
              {this.props.hasItemsWithPendingConfirmation && (
                <Alert
                  className="cart-items__card-container-alert"
                  hideClose={true}
                  type="warning-light"
                  isVisible={true}
                >
                  <div className="col-grid direction-row col-bleed">
                    <I18nText id="ibe.automatic_products.added" />
                    <Button
                      className="cart-items__card-container-alert-button"
                      version="v2"
                      size="small"
                      onClick={() => {
                        this.firstConfirmableItem.scrollIntoView({ behavior: 'smooth' });
                      }}
                      label={
                        <I18nText id="ibe.automatic_products.review" returnStringOnly={true} />
                      }
                    />
                  </div>
                </Alert>
              )}
              {this.duplicateItemOfSameSearchExist() && this.props.canBookQuoteItems && (
                <div className="col-12 col-grid col-bleed cart-items__card-container-alert">
                  <Alert type="warning-light" hideClose={true} isVisible={true}>
                    <I18nText
                      id="ibe.book_quote.only_one_item_per_search"
                      returnStringOnly={true}
                    />
                  </Alert>
                </div>
              )}

              {this.props.items.length ? (
                this.props.items.map((item, index) => (
                  <div
                    ref={
                      item.pendingConfirmation && !this.firstConfirmableItem
                        ? this.setFirstConfirmableItem
                        : undefined
                    }
                    key={item.id}
                  >
                    <CartItemWrapper
                      isCartLocked={isCartLocked}
                      item={item}
                      itemTitle={this.props.title}
                      itemNo={index + 1}
                      journeyElement={item.journeyElement}
                      flightResultDetailsById={this.props.flightResultDetailsById}
                      flightServicesByResultId={this.props.flightServicesByResultId}
                      showFlightSeatsStatus={true}
                      showServices={this.props.showServices}
                      removeFromCart={this.props.removeFromCart}
                      confirmAddition={this.props.confirmAddition}
                      travelerCount={this.props.travelerCount}
                      staticProducts={this.props.staticProducts}
                      setStaticProductQuantity={this.props.setQuantity}
                      setOutOfPolicyJustification={this.props.setOutOfPolicyJustification}
                      shouldLockScroll={this.state.shouldLockScroll}
                      setShouldLockScroll={state => this.setState({ shouldLockScroll: state })}
                      travelers={this.props.travelers}
                      canExchange={canExchange}
                      pastJourneyElement={this.props.pastJourneyElement}
                      isExchangedItemAvailable={this.props.exchangeQuoteData?.availability}
                      exchangeFareBrandMap={this.props.exchangeQuoteData?.fareBrands}
                    />
                  </div>
                ))
              ) : (
                <EmptyCart />
              )}
              {!isPassengerCodeAvailable && !canExchange && (
                <AddButtons
                  isCartLocked={isCartLocked}
                  hasStaticProducts={this.props.hasStaticProducts}
                />
              )}
            </div>
            {canExchange ? (
              <ExchangeCartSummary
                exchangeQuoteData={this.props.exchangeQuoteData}
                exchangeQuoteStatus={this.props.exchangeQuoteStatus}
                trips={this.props.pastJourneyElement.trips}
              />
            ) : (
              <div ref={this.orderSummaryRef} className="col-lg-4 col-grid row-gap-20">
                <Card className="cart-side-nav" version="v2">
                  <Summary
                    items={this.props.items}
                    staticProducts={this.props.staticProducts}
                    total={this.props.total}
                    taxes={this.props.taxes}
                    voucher={this.props.voucher}
                    travelerCount={this.props.travelerCount}
                    fees={feeList(
                      this.props.fees,
                      this.props.cartFees,
                      this.props.intl,
                      !this.context.laymanMode,
                      I18n.t('components.ibe.summary.additional_taxes_services_and_charges'),
                    )}
                    currency={this.props.currency}
                    paymentMethodSurcharge={this.props.paymentMethodSurcharge}
                    grossTotalsByGroup={this.props.grossTotalsByGroup}
                    isFetchingCart={false}
                    isFetchingFop={false}
                    totalWithoutVoucherReduction={this.props.totalWithoutVoucherReduction}
                  />
                </Card>
                {this.cartSharingPossible() &&
                  this.props.items.length > 0 &&
                  (this.context.freePlan ? (
                    <Tooltip content={toolTipContent} type="inverse" position="top">
                      <Card version="v2" className="cart-share__free-user">
                        {cartShareComponent}
                      </Card>
                    </Tooltip>
                  ) : (
                    <Card version="v2">{cartShareComponent}</Card>
                  ))}
                {!this.context.laymanMode && !this.context.offerOnly && (
                  <Card version="v2" shadow="dark">
                    <CartDeal
                      dealName={this.props.dealName}
                      dealTeaser={this.props.dealTeaser}
                      setDealName={this.props.setDealName}
                      setDealTeaser={this.props.setDealTeaser}
                      dealSaveAvailable={this.props.dealSaveAvailable}
                      hasAutomaticallyAddedStaticProducts={
                        this.props.hasAutomaticallyAddedStaticProducts
                      }
                      saveDeal={this.props.saveDeal}
                      forceDeal={this.props.forceDeal}
                      items={this.props.items}
                    />
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

Cart.defaultProps = {
  canBookQuoteItems: false,
  expiredAt: null,
  flightResultDetailsById: {},
  staticProducts: [],
  cartState: null,
  title: '',
  sharedTitle: '',
  fareBasisInfo: [],
  showServices: true,
  paymentMethodSurcharge: null,
  totalCartItems: 0,
  initialSearchParams: {},
  pastJourneyElement: {},
  exchangeQuoteData: null,
  exchangeQuoteStatus: null,
};

Cart.propTypes = {
  journeyElements: PropTypes.shape({
    flight: PropTypes.shape({}),
    hotel: PropTypes.shape({}),
    car: PropTypes.shape({}),
  }).isRequired,
  showServices: PropTypes.bool,
  canBookQuoteItems: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  totalCartItems: PropTypes.number,
  expiredAt: PropTypes.string,
  frozen: PropTypes.bool.isRequired,
  items: PropTypes.shape([]).isRequired,
  isShared: PropTypes.bool.isRequired,
  sharedByCurrentPerson: PropTypes.bool.isRequired,
  flightServicesByResultId: PropTypes.shape({ [PropTypes.string]: PropTypes.shape({}) }).isRequired,
  voucher: PropTypes.shape({}).isRequired,
  intl: intlShape.isRequired,
  taxes: PropTypes.shape([]).isRequired,
  fees: PropTypes.shape([]).isRequired,
  cartFees: PropTypes.shape([]).isRequired,
  total: PropTypes.string.isRequired,
  title: PropTypes.string,
  sharedTitle: PropTypes.string,
  currency: PropTypes.string.isRequired,
  grossTotalsByGroup: PropTypes.string.isRequired,
  isFetchingFop: PropTypes.string.isRequired,
  dealName: PropTypes.string.isRequired,
  dealTeaser: PropTypes.string.isRequired,
  setDealName: PropTypes.func.isRequired,
  fetchFees: PropTypes.func.isRequired,
  setDealTeaser: PropTypes.func.isRequired,
  dealSaveAvailable: PropTypes.bool.isRequired,
  saveDeal: PropTypes.func.isRequired,
  saveApprovalRequest: PropTypes.func.isRequired,
  removeFromCart: PropTypes.func.isRequired,
  forceDeal: PropTypes.bool.isRequired,
  cartId: PropTypes.string.isRequired,
  cartUrl: PropTypes.string.isRequired,
  forceApprovalRequest: PropTypes.bool.isRequired,
  continuePossible: PropTypes.bool.isRequired,
  addStaticProductToCart: PropTypes.func.isRequired,
  allItemsAvailable: PropTypes.bool.isRequired,
  travelerCount: PropTypes.objectOf(
    PropTypes.shape({
      adults: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      children: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      infants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }),
  ).isRequired,
  flightResultDetailsById: PropTypes.objectOf(
    PropTypes.shape({
      available: PropTypes.oneOf([true, false, null]),
    }),
  ),
  fetchStaticProducts: PropTypes.func.isRequired,
  setQuantity: PropTypes.func.isRequired,
  staticProducts: PropTypes.arrayOf(staticProductShape),
  cartText: PropTypes.string.isRequired,
  setCartText: PropTypes.func.isRequired,
  cartState: PropTypes.oneOf(['open', 'paid', 'booked']),
  hasStaticProducts: PropTypes.bool.isRequired,
  flightServicesFetched: PropTypes.bool.isRequired,
  fareBasisInfo: PropTypes.arrayOf(PropTypes.shape({})),
  hasItemsWithPendingConfirmation: PropTypes.bool.isRequired,
  hasAutomaticallyAddedStaticProducts: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  paymentMethodSurcharge: PropTypes.shape({
    title: PropTypes.string,
    amount: PropTypes.string,
    currency: PropTypes.string,
  }),
  confirmAddition: PropTypes.func.isRequired,
  setOutOfPolicyJustification: PropTypes.func.isRequired,
  totalWithoutVoucherReduction: PropTypes.string.isRequired,
  travelers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  exchange: PropTypes.shape({
    canExchange: PropTypes.bool,
    reservationItemIdentifier: PropTypes.string,
  }).isRequired,
  initialSearchParams: PropTypes.shape(initialSearchParamsShape),
  airports: AirportShape.isRequired,
  pastJourneyElement: PropTypes.shape({
    trips: PropTypes.arrayOf(
      PropTypes.shape({
        originCode: PropTypes.string,
        destinationCode: PropTypes.string,
      }),
    ),
  }),
  exchangeQuoteData: PropTypes.shape({
    availability: PropTypes.bool,
    fareBrands: PropTypes.shape({
      [PropTypes.number]: PropTypes.string,
    }),
  }),
  fetchExchangeQuote: PropTypes.func.isRequired,
  exchangeQuoteStatus: PropTypes.string,
};

Cart.contextTypes = {
  allowBookingOfUnapprovedItems: PropTypes.bool.isRequired,
  laymanMode: PropTypes.bool.isRequired,
  iframeMode: PropTypes.bool.isRequired,
  travelerLoggedIn: PropTypes.bool.isRequired,
  callbackParams: PropTypes.shape({}).isRequired,
  offerOnly: PropTypes.bool.isRequired,
  travelerHasOrganization: PropTypes.bool.isRequired,
  travelArranger: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    customerInformationVisible: PropTypes.bool.isRequired,
  }).isRequired,
  frozen: PropTypes.bool.isRequired,
  freePlan: PropTypes.bool.isRequired,
};

export default Cart;
